import type { MeshData } from "@cfg/kernel";
import type { BuildResult } from "@cfg/kernel/worker";
import { createStore as createIdb, del, get, keys, set } from "idb-keyval";

/**
 * Two-level result cache: an in-memory map for the session and IndexedDB so a
 * reopened saved configuration renders instantly on the next visit.
 */
const MEMORY_LIMIT = 40;
const IDB_LIMIT = 200;

interface Stored {
  positions: Float32Array;
  normals?: Float32Array;
  indices: Uint32Array;
  bbox: MeshData["bbox"];
  numTri: number;
  stats: BuildResult["stats"];
  issues: BuildResult["issues"];
  dimensions?: BuildResult["dimensions"];
  at: number;
}

export interface MeshCacheOptions {
  /** `false` turns the cache into a pass-through (design studio: source edits keep the same version). */
  enabled?: boolean;
}

export class MeshCache {
  private memory = new Map<string, BuildResult>();
  private readonly enabled: boolean;
  private idb: ReturnType<typeof createIdb> | null;

  constructor({ enabled = true }: MeshCacheOptions = {}) {
    this.enabled = enabled;
    this.idb = enabled && typeof indexedDB !== "undefined" ? createIdb("cfg-mesh-cache", "results") : null;
  }

  async get(key: string): Promise<BuildResult | undefined> {
    if (!this.enabled) return undefined;
    const m = this.memory.get(key);
    if (m) {
      this.memory.delete(key);
      this.memory.set(key, m); // LRU bump
      return m;
    }
    if (!this.idb) return undefined;
    try {
      const s = (await get<Stored>(key, this.idb)) as Stored | undefined;
      if (!s) return undefined;
      const result: BuildResult = {
        mesh: { positions: s.positions, normals: s.normals, indices: s.indices, bbox: s.bbox, numTri: s.numTri },
        stats: s.stats,
        issues: s.issues,
        dimensions: s.dimensions ?? [],
      };
      this.remember(key, result);
      return result;
    } catch {
      return undefined;
    }
  }

  remember(key: string, result: BuildResult): void {
    if (!this.enabled) return;
    this.memory.set(key, result);
    while (this.memory.size > MEMORY_LIMIT) {
      const oldest = this.memory.keys().next().value;
      if (oldest === undefined) break;
      this.memory.delete(oldest);
    }
  }

  async put(key: string, result: BuildResult): Promise<void> {
    this.remember(key, result);
    if (!this.idb) return;
    try {
      const s: Stored = {
        positions: result.mesh.positions,
        normals: result.mesh.normals,
        indices: result.mesh.indices,
        bbox: result.mesh.bbox,
        numTri: result.mesh.numTri,
        stats: result.stats,
        issues: result.issues,
        dimensions: result.dimensions,
        at: Date.now(),
      };
      await set(key, s, this.idb);
      void this.trim();
    } catch {
      // quota or private mode; the memory tier still works
    }
  }

  private trimming = false;
  private async trim(): Promise<void> {
    if (!this.idb || this.trimming) return;
    this.trimming = true;
    try {
      const all = await keys<string>(this.idb);
      if (all.length <= IDB_LIMIT) return;
      const dated: { key: string; at: number }[] = [];
      for (const k of all) {
        const s = (await get<Stored>(k, this.idb)) as Stored | undefined;
        dated.push({ key: k, at: s?.at ?? 0 });
      }
      dated.sort((a, b) => a.at - b.at);
      for (const d of dated.slice(0, all.length - IDB_LIMIT + 20)) await del(d.key, this.idb);
    } finally {
      this.trimming = false;
    }
  }
}
